/*
 * Copyright (c) 2020. American Express Company
 */

// theirs
import _ from 'lodash';
import { LocalDate } from '@js-joda/core';
import React, { Component, ReactComponentElement } from 'react';

// models and services
import { ExpenseDrawerViewModel } from 'src/types/view-models/expense-drawer.view.model';
import { RecurringExpenseModel } from 'src/types/recurring-expense.model';
import LiteralsService from 'src/services/literals/LiteralsService';
import RecurringExpenseService from '../../services/recurring-expense.service';

// components
import BottomButtons from 'src/components/bottom-buttons/BottomButtons';
import BWCurrencyInput from 'src/bw/bw-currency-input/BW-Currency-Input';
import Drawer from 'src/components/drawer/Drawer';
import { FieldControl, Hint, IconOpenBanking, Label } from '@americanexpress/dls-react';
import InfoCallout from 'src/components/callout/InfoCallout';
import OptionsBox from 'src/components/options-box/OptionsBox';

// icons and css
import './ExpenseDrawer.scss';
import NavigationService from 'src/services/navigation.service';

export default class ExpenseDrawer extends Component<
    ExpenseDrawerViewModel,
    {
        recurringExpense: RecurringExpenseModel;
        expense: number;
    }
> {
    /// ////////////////
    // Onced Methods //
    /// ////////////////
    private readonly handleDeleteOnce: () => void = _.once(async () => await this.handleDelete());
    private readonly handleSaveOnce: () => void = _.once(async () => await this.handleSave());

    public override state = {
        recurringExpense: null as RecurringExpenseModel,
        expense: 0
    };

    public static getDerivedStateFromProps(props: any, prevState: any): any {
        let recurringExpense: RecurringExpenseModel = null;
        let expense = 0;

        if (!prevState.recurringExpense) {
            if (props.drawerProps.currentRecurringExpense) {
                recurringExpense = props.drawerProps.currentRecurringExpense;
                expense = recurringExpense.monthlyAmount;
            } else {
                recurringExpense = new RecurringExpenseModel();
                recurringExpense.startDate = LocalDate.now();
                recurringExpense.monthlyAmount = 0;
                recurringExpense.name = 'Recurring Expenses';
                recurringExpense.categories = 'Other';
            }
        } else {
            return prevState;
        }

        return { recurringExpense, expense };
    }

    public override render(): ReactComponentElement<any> {
        return (
            <Drawer
                drawerProps={this.props.drawerProps}
                withBackButton
                handleBack={this.props.handleBack}
                title={LiteralsService.getLiteral('ExpenseDrawer.title')}
            >
                <OptionsBox
                    headerText={LiteralsService.getLiteral('ExpenseDrawer.optionsBox.header')}
                    icon={<IconOpenBanking />}
                    content={[
                        LiteralsService.getLiteral('ExpenseDrawer.optionsBox.content1'),
                        LiteralsService.getLiteral('ExpenseDrawer.optionsBox.content2')
                    ]}
                    isBlue
                    hasPlaidButton
                    handleSuccess={this.handleSuccess.bind(this)}
                    firstImport={this.props.firstImport}
                    handleAccounts={this.props.handleAccounts.bind(this)}
                    handleClearFirstImport={this.props.handleClearFirstImport.bind(this)}
                    handleFirstImport={this.props.handleFirstImport.bind(this)}
                    setDrawer={this.props.setDrawer.bind(this)}
                    user={this.props.user}
                />

                <div className="drawerSection">
                    <FieldControl>
                        <Label htmlFor="expenseAmount">{LiteralsService.getLiteral('ExpenseDrawer.inputLabel')}</Label>
                        <Hint>{LiteralsService.getLiteral('ExpenseDrawer.inputHint')}</Hint>
                        <BWCurrencyInput
                            fullWidth
                            id="expenseAmount"
                            defaultValue={0}
                            status=""
                            value={this.state.recurringExpense.monthlyAmount}
                            showFractions={false}
                            onChange={(value: number) => {
                                this.handleChange(value, 'monthlyAmount');
                            }}
                        />
                    </FieldControl>
                    <InfoCallout noMargin text={LiteralsService.getLiteral('ExpenseDrawer.callout')} />
                </div>

                <BottomButtons
                    drawerProps={this.props.drawerProps}
                    showLoader={this.props.showLoader}
                    hideLoader={this.props.hideLoader}
                    handleUpdateCard={() => this.handleSaveOnce()}
                    handleDeleteCard={() => {
                        this.props.handleOpenConfirmation({
                            onAction: this.handleDeleteOnce.bind(this),
                            action: 'delete',
                            object: 'manual'
                        });
                    }}
                    handleBack={this.props.handleBack}
                    primaryButtonText={LiteralsService.getLiteral('ExpenseDrawer.updateButton')}
                />
            </Drawer>
        );
    }

    private async handleSave(): Promise<void> {
        if (this.state.recurringExpense.id) {
            await RecurringExpenseService.updateRecurringExpense(this.state.recurringExpense);
        } else {
            await RecurringExpenseService.createRecurringExpense(this.state.recurringExpense);
        }

        await this.props.handleReloadUser();
    }

    private async handleDelete(): Promise<void> {
        await RecurringExpenseService.deleteRecurringExpense(this.state.recurringExpense);

        await this.props.handleReloadUser();
        await NavigationService.goTo(this.props.history, '/drawer/profile');
    }

    private handleChange(value: any, name: string, type: string = 'none'): void {
        const recurringExpense: RecurringExpenseModel = { ...this.state.recurringExpense };
        if (type === 'currency') {
            (recurringExpense as any)[name] = Number(value.replace(/[$,]/g, ''));
        } else {
            (recurringExpense as any)[name] = value;
        }

        this.setState({
            recurringExpense
        });
    }

    private async handleSuccess(): Promise<void> {
        await NavigationService.goBack(this.props.history);
    }
}
